import { Component, h, Prop, Watch, State, EventEmitter, Event, Element, Host, Listen } from '@stencil/core';
import { TiMetricsActionEventDetail, TiResponsive, TiResponsiveHandler, TiResponsiveViewport } from '@ticom/web-component-utils';
import { HTMLTiDialogElement } from '@ticom/ui-components/dist';
import { MediaGalleryItemChangeEventDetail, MediaGalleryItemControlEventDetail, MediaPlayerStatus } from './media-gallery.interface';

/**
 * @slot download-label - Text for the dialog's image "download" button.
 * @slot dialog-title-video - Dialog title for videos.
 * @slot media - Image and video elements.
 * @slot video-transcript-link-text - Text for the video "view transcript" link.
 */
@Component({
    tag: 'ti-media-gallery',
    styleUrl: 'media-gallery.scss',
    shadow: true
})
export class MediaGallery implements TiResponsive {

    /* Scroll wrapper element for the main display*/
    private _carouselScrollElement: HTMLElement;
    /* Device detection based on user-agent */
    private _isMobileDevice: boolean = false;
    /* Live node list of all child media elements */
    private _mediaElements: NodeListOf<HTMLElement>;
    /* List of enabled media elements */
    private _mediaElementsVisible: HTMLElement[];
    /* IntersectionObserver to sync bullets with currently visible image */
    private _intersectionObserver: IntersectionObserver;
    /* zoom gallery carousel element */
    private _modalCarouselElement: HTMLElement;
    /* full gallery zoom modal element */
    private _modalElement: HTMLTiDialogElement;
    private _modalOpen: boolean = false;
    /* zoom gallery carousel scroll element */
    private _modalScrollElement: HTMLElement;
    /* Count of visible images */
    private _visibleImagesCount: number;


    @Element() hostElement: HTMLElement;

    /**
     * Trigger a render when slotted media fires a change event
     */
    @State() mediaChanged: number = 0;

    /**
     * Keep track of component status
     *
     * @type {string}
     * @default 'LOADING'
     */
    @State() status: string = MediaPlayerStatus.LOADING;

    /**
     * The current viewport size tracked by TiResponsiveHandler
     *
     * @type {TiResponsiveViewport}
     * @memberof MediaGallery
     */
    @TiResponsiveHandler()
    @State() viewportSize: TiResponsiveViewport;
    @Watch('viewportSize')
    handleViewportSizeChange() {
        setTimeout(() => {
            if (this.viewportSize === TiResponsiveViewport.phone) {
                // IO to handle scroll detection of which item is active
                this._addIntersectionObserver();
            } else {
                this._removeIntersectionObserver();
                // show all the thumbnails if current image is past the '+n' on the last thumbnail
                if (this.selectedIndex > this._visibleImagesCount - 1) {
                    this.showAllThumbnails = true;
                }
            }
        }, 200);
    }

    /**
     * Property for caption text associated with the gallery and dialog.
     */
    @Prop() captionText?: string;

    /**
     * Property for setting number of thumbnails per row
     */
    @Prop({ mutable: true }) columns: number = 4;
    @Watch('columns')
    handleColumnsChange(newValue: number) {
        this.columns = newValue;
    }

    /**
     * Property to determine which item is selected
     */
    @Prop({ mutable: true }) selectedIndex = 0;

    /**
     * Property to show all the thumbnails instead of default view
     * with '+n' on the last thumbnail
     */
    @Prop({ mutable: true }) showAllThumbnails?: boolean = false;

    /**
     * Property to display a download link for the image in the dialog.
     */
    @Prop() showDownloadLink?: boolean = false;

    /**
     * Event emitted for metrics
     */
    @Event() tiMetricsAction: EventEmitter<TiMetricsActionEventDetail>;

    componentWillLoad() {
        // Get all elements that are slotted in as "media"
        this._isMobileDevice = this._detectMobileDevice();
        this._mediaElements = this.hostElement.querySelectorAll('[slot="media"]');

        // Media: Only Videos - Initially HIDE Media Gallery until video data has been fetched
        // to prevent it showing and then hiding on the page
        // Check if other media types other than videos have been slotted in
        const hasOnlyVideos = ![...this._mediaElements].some((el) => el.dataset.mediaType != 'video');
        if (hasOnlyVideos) {
            this.status = MediaPlayerStatus.LOADING;
        } else {
            this.status = MediaPlayerStatus.READY;
        }

        this._setVisibleMedia();
    }

    /**
     * Add IntersectionObserver to sync bullets with currently visible image
     * Add MutationObserver to the slotted videoplayers to rerender when api finishes fetch
     */
    componentDidLoad() {
        if (this.status === MediaPlayerStatus.LOADING) {
            return;
        }
        if (this.viewportSize === TiResponsiveViewport.phone) {
            this._addIntersectionObserver();
        }
        //Reset view as scroll position is cached
        this._carouselScrollElement.scrollTo({ behavior: 'auto', left: 0 });
    }

    disconnectedCallback() {
        this._removeIntersectionObserver();
    }

    /**
     * Add window resize listener when gallery zoom modal opened
     */
    @Listen('tiDialogOpened')
    onTiDialogOpened() {
        window.addEventListener('resize', this._carouselScrollUpdate, {
            passive: true
        });
        this._modalScrollElement.addEventListener('scroll', this._carouselScrollUpdate, false);
        this._modalOpen = true;
    }

    /**
     * Remove window resize listener when gallery zoom modal closed
     */
    @Listen('tiDialogClosed')
    onTiDialogClosed() {
        window.removeEventListener('resize', this._carouselScrollUpdate);
        this._modalScrollElement.removeEventListener('scroll', this._carouselScrollUpdate, false);
        this._modalOpen = false;

        // pause video when closed
        this._mediaElementsVisible[this.selectedIndex].dispatchEvent(new CustomEvent<MediaGalleryItemControlEventDetail>(
            'tiMediaGalleryItemControl',
            { detail: { action: 'pause' } }
        ));
    }

    /**
     * Listen for a tiMediaLoaded event to determine if the media item should be kept in the gallery.
     * Allows for lazy initialization of media such as in ti-video-player.
     */
    @Listen('tiMediaGalleryItemChange')
    onMediaGalleryItemChange(ev: CustomEvent<MediaGalleryItemChangeEventDetail>) {
        // Eliminate media that has been disabled after creation or re-enable disabled media
        // check for property presence not truthiness
        if (ev.detail.disabled != null) {
            if (ev.detail.disabled) {
                (ev.target as HTMLElement).dataset.mediaDisabled = 'true';
            }
            else {
                delete (ev.target as HTMLElement).dataset.mediaDisabled;
                this.status = MediaPlayerStatus.READY;
            }
            // reset visible media elements filtering filter out elements with the mediaDisabled data property
            this._setVisibleMedia();
        }

        // handle fullscreen changes
        if (ev.detail.fullscreen != null) {
            this._setDialogBlockingOnFullscreen(ev.detail.fullscreen);
        }

        // trigger render
        this.mediaChanged++;
    }

    /**
     * Add IntersectionObserver to sync bullets with currently visible image
     */
    private _addIntersectionObserver() {
        if ('IntersectionObserver' in window && !this._intersectionObserver && this._carouselScrollElement) {
            const items = Array.from(this._carouselScrollElement.children);
            if (items.length > 0) {
                this._intersectionObserver = new IntersectionObserver((carouselImages: any) => {
                    carouselImages.forEach(img => {
                        // when the observer detects an image changing
                        // and the image is intersecting,
                        // get the intersecting image's index
                        // set the current image, so the state updates and render fires,
                        // so corresponding bullet styling updates
                        if (img.isIntersecting) {
                            const index = items.indexOf(img.target);
                            if (index != this.selectedIndex) {
                                this._updateActiveMediaElement(index);
                            }
                        }
                    });
                }, {
                    // create an observer with the carousel as intersection root
                    root: this._carouselScrollElement, threshold: 0.5
                });

                // observe each item
                items.forEach(image => {
                    this._intersectionObserver.observe(image);
                });
            }
        }
    }

    /**
     * Do carousel scroll button clicks
     * @param 'left' or 'right'
     */
    private _carouselScroll(direction: string) {
        const carouselThumbnailWidth = this._modalScrollElement.children[0].clientWidth;
        const carouselThumbnailScrollPosition = this._modalScrollElement.scrollLeft;
        const scrollDistance = (direction === 'left')
            ? carouselThumbnailScrollPosition - (carouselThumbnailWidth * 2.5)
            : carouselThumbnailScrollPosition + (carouselThumbnailWidth * 2.5);
        this.tiMetricsAction.emit({
            elementName: this.hostElement.tagName,
            eventName: 'click',
            data: {
                value: direction + ' scroll'
            }
        });
        this._modalScrollElement.scroll({
            behavior: 'smooth',
            left: scrollDistance,
            top: 0
        });

        // update style for chevron and shadow immediately when leaving full left or right
        if (this._checkCarouselScrollPosition(this._modalScrollElement) != direction) {
            this._modalCarouselElement.className = `ti-media-gallery-dialog-carousel ti-media-gallery-dialog-carousel-is-scrollable ti-media-gallery-dialog-carousel-is-between`;
        }
    }

    /**
     * Check to see if element is actually overflowing
     */
    private _checkCarouselOverflow({ clientWidth, scrollWidth }) {
        return scrollWidth > clientWidth;
    }

    /**
     * Check to see if element is actually overflowing
     */
    private _checkCarouselScrollPosition = ({ clientWidth, scrollWidth, scrollLeft }) => {
        const maxScrollLeft = scrollWidth - clientWidth;
        const scrollPosition = (scrollLeft === 0) ? 'left' : (scrollLeft === maxScrollLeft) ? 'right' : 'between';
        return scrollPosition;
    }

    /**
     * Debounce helper function for window resize listener
     */
    private _debounce(func: () => unknown, wait: number, immediate?: boolean) {
        let timeout;
        return function () {
            // eslint-disable-next-line @typescript-eslint/no-this-alias
            const context = this;
            // eslint-disable-next-line prefer-rest-params
            const args = arguments;
            const later = function () {
                timeout = null;
                if (!immediate) {
                    func.apply(context, args);
                }
            };
            const callNow = immediate && !timeout;
            clearTimeout(timeout);
            timeout = setTimeout(later, wait);
            if (callNow) {
                func.apply(context, args);
            }
        };
    }

    /**
     * Detect mobile device based on user-agent
     * Based on: detectmobilebrowsers.com
     * https://stackoverflow.com/questions/11381673/detecting-a-mobile-browser
     */
    private _detectMobileDevice() {
        const a = navigator.userAgent;
        return /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(a)
            || /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw-(n|u)|c55\/|capi|ccwa|cdm-|cell|chtm|cldc|cmd-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc-s|devi|dica|dmob|do(c|p)o|ds(12|-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(-|_)|g1 u|g560|gene|gf-5|g-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd-(m|p|t)|hei-|hi(pt|ta)|hp( i|ip)|hs-c|ht(c(-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i-(20|go|ma)|i230|iac( |-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|-[a-w])|libw|lynx|m1-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|-([1-8]|c))|phil|pire|pl(ay|uc)|pn-2|po(ck|rt|se)|prox|psio|pt-g|qa-a|qc(07|12|21|32|60|-[2-7]|i-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h-|oo|p-)|sdk\/|se(c(-|0|1)|47|mc|nd|ri)|sgh-|shar|sie(-|m)|sk-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h-|v-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl-|tdg-|tel(i|m)|tim-|t-mo|to(pl|sh)|ts(70|m-|m3|m5)|tx-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas-|your|zeto|zte-/i.test(a.substring(0, 4));
    }

    /**
     * Small width only - On bullet click, scroll to active image and highlight that bullet
     */
    private _bulletClick(index) {
        this._updateActiveMediaElement(index);
        if (this._carouselScrollElement) {
            this._carouselScrollElement.children[this.selectedIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
        this._trackThumbnailClickMetrics();
    }

    /**
     * On resize Handler for carousel update on scroll and resize
     */
    private _carouselScrollUpdate = this._debounce(() => {
        this._updateThumbnailCarouselButtons();
    }, 200);

    /**
     * Do carousel scroll button clicks
     */
    private _chevronClick(buttonDirection: string) {
        this._updateActiveMediaElement((buttonDirection === 'left') ? this.selectedIndex - 1 : this.selectedIndex + 1);

        // DESKTOP - show all images if on the last visible image
        if (this.viewportSize === TiResponsiveViewport.desktop) {
            this.showAllThumbnails ||= this.selectedIndex === (this.columns - 1);
        }
    }

    /**
     * Handle keyboard navigation on focus
     */
    private _handleKeyDown = (event: KeyboardEvent) => {
        const keyActions = {
            ArrowLeft: () => this._chevronClick('left'),
            ArrowRight: () => this._chevronClick('right'),
            ' ': () => this._openGallerydialog(),
            Enter: () => this._openGallerydialog()
        };

        const action = keyActions[event.key];

        if (action) {
            event.preventDefault();
            action(event);
        }
    }

    /**
     * Prevent dialog from closing when fullscreen media is overlaying over the dialog by setting it to blocking.
     * On mobile, close dialog when fullscreen mode is exited.
     * (ex: When user clicks the button to exit fullscreen in the brightcove player they trigger closing the dialog
     *  since the button sits outside of the dialog content area)
     */
    private _setDialogBlockingOnFullscreen(fullscreen: boolean) {

        // Set dialog to blocking when video is overlaying over dialog in fullscreen
        if (this._modalOpen && fullscreen) {
            this._modalElement.setAttribute('blocking', 'true');
        } else {
            this._modalElement.setAttribute('blocking', 'false');
        }

        // Phone close dialog when exiting fullscreen to bypass dialog screen
        if (this._isMobileDevice && !fullscreen) {
            this._modalElement.close();
        }
    }

    /**
     * Wide width mode only - On thumbnail click, set active image and highlight it
     */
    private _thumbnailImageClick(event, index) {
        this._updateActiveMediaElement(index);
        this._trackThumbnailClickMetrics();

        // View all thumbnails if thumbnail selected past overflow on dialog
        if (index >= this.columns) {
            this.showAllThumbnails = true;
        }

        // if in the modal view, scroll thumbnail into view if it's outside
        if (this._modalOpen) {
            // needs to be parent of target to account for border
            event.target.parentElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
    }

    /**
     * Desktop: Opens dialog with Image/Video title and description transcript link
     * Mobile: Bypasses dialog - image opens in new tab, Videos - play in fullscreen
     */
    private _openGallerydialog() {
        // Need to account for phone in landscape breakpoint
        const selectedMedia = this._mediaElementsVisible[this.selectedIndex] as HTMLElement;
        const selectedMediaIsVideo = selectedMedia.dataset.mediaType === 'video';


        // Mobile - Open Image in New Tab
        if (this._isMobileDevice && !selectedMediaIsVideo) {
            const srcUrl = selectedMedia.dataset.mediaImageUrl ?? selectedMedia.dataset.mediaThumbnailUrl;
            this._openImageInNewTab(srcUrl);
            return;
        }

        // Mobile still needs dialog to open as video player is slotted inside dialog
        this._modalElement.open();

        // Bring active media element in view
        this._updateActiveMediaElement(this.selectedIndex);

        // Play active video via the event API
        if (selectedMediaIsVideo) {
            selectedMedia.dispatchEvent(new CustomEvent<MediaGalleryItemControlEventDetail>(
                'tiMediaGalleryItemControl',
                { detail: { action: 'play', fullscreen: this._isMobileDevice } }
            ));
        }

        // fire metrics event
        this._trackImageClickMetrics();
    }

    /**
     * Mobile REQ: Open image in a new window
     * @param src
     */
    private _openImageInNewTab(src) {
        this._trackImageClickMetrics();
        // open blank window and add HTML instead of opening with the URL specified,
        // otherwise The new window opens with black background
        const win = window.open('', '_blank');
        win.document.write('<html>');
        win.document.write('<head><title>TI Image</title></head>');
        win.document.write(`<body><img src="${src}" style="width: 100%;"></body>`);
        win.document.write('</html>');
    }

    /**
     * Remove intersection observer
     */
    private _removeIntersectionObserver() {
        if (this._intersectionObserver) {
            this._intersectionObserver.disconnect();
            this._intersectionObserver = null;
        }
    }

    /**
     * Find all slotted "media" elements that are not disabled.
     */
    private _setVisibleMedia() {
        this._mediaElementsVisible = [];
        Array.from(this._mediaElements, el => {
            if (el.dataset.mediaDisabled !== 'true') {
                this._mediaElementsVisible.push(el);
            }
        });
    }

    /**
     * Fire metrics event on active image click
     */
    private _trackImageClickMetrics() {
        const activeMediaItem = this._mediaElementsVisible[this.selectedIndex];

        this.tiMetricsAction.emit({
            elementName: this.hostElement.tagName,
            eventName: `${activeMediaItem.dataset.mediaType} click`,
            data: {
                value: 'expand',
                column: activeMediaItem.dataset.mediaName
            }
        });
    }

    /**
     * Fire metrics event on thumbnail click
     */
    private _trackThumbnailClickMetrics() {
        this.tiMetricsAction.emit({
            elementName: this.hostElement.tagName,
            eventName: 'image click',
            data: {
                value: `${this._mediaElementsVisible[this.selectedIndex].dataset.mediaType} thumbnail`,
                column: this._mediaElementsVisible[this.selectedIndex].dataset.mediaName
            }
        });
    }

    /**
     * Fire metrics event on file-download
     */
    private _trackFileDownloadMetrics() {
        this.tiMetricsAction.emit({
            elementName: this.hostElement.tagName,
            eventName: 'link click',
            data: {
                value: `image download|${this._mediaElementsVisible[this.selectedIndex].dataset.mediaName}`
            }
        });
    }

    /**
     * Fire metrics event when view transcript link is clicked
     */
    private _trackViewTranscriptMetrics() {
        this.tiMetricsAction.emit({
            elementName: this.hostElement.tagName,
            eventName: 'link click',
            data: {
                value: `view transcript|${this._mediaElementsVisible[this.selectedIndex].dataset.mediaName}`
            }
        });
    }

    /**
     * Brings the active media element into view on both Carousel and Dialog
     * @param index
     */
    private _updateActiveMediaElement(index: number) {
        // pause previous video when item changes
        if (this._mediaElementsVisible[this.selectedIndex].dataset.mediaType === 'video') {
            this._mediaElementsVisible[this.selectedIndex].dispatchEvent(new CustomEvent<MediaGalleryItemControlEventDetail>(
                'tiMediaGalleryItemControl',
                { detail: { action: 'pause' } }
            ));
        }

        // Carousel
        this._carouselScrollElement?.children[index].scrollIntoView({ behavior: `${this._modalOpen ? 'auto' : 'smooth'}`, block: 'nearest' });

        // Dialog - show active element
        this._mediaElementsVisible.forEach((el) => {
            el.style.display = 'none';
        });
        this._mediaElementsVisible[index].style.display = 'block';

        // start video if gallery dialog is open
        if (this._modalOpen && this._mediaElementsVisible[index].dataset.mediaType === 'video') {
            this._mediaElementsVisible[index].dispatchEvent(new CustomEvent<MediaGalleryItemControlEventDetail>(
                'tiMediaGalleryItemControl',
                { detail: { action: 'play' } }
            ));
        }

        // update index
        this.selectedIndex = index;
    }

    /**
     * Update chevron appearances on modal carousel based on scroll pos and overflow
     */
    private _updateThumbnailCarouselButtons() {
        const scrollPosition = this._checkCarouselScrollPosition(this._modalScrollElement);
        const classNameBase = `ti-media-gallery-dialog-carousel`;
        const classNamePosition = `${classNameBase} ti-media-gallery-dialog-carousel-is-scrollable ti-media-gallery-dialog-carousel-is-${scrollPosition}`;
        // sets the classname based on scroll position
        this._modalCarouselElement.className = (this._checkCarouselOverflow(this._modalScrollElement)) ? classNamePosition : classNameBase;
    }


    render() {
        if (this.status === MediaPlayerStatus.LOADING) {
            return <Host style={{ display: 'none' }}></Host>;

        } else if (this.status === MediaPlayerStatus.READY) {

            const _baseJsxKey = (el) => `${el.dataset.mediaName}-${el.dataset.mediaDesc}-${el.dataset.mediaImageUrl}-${el.dataset.mediaThumbnailUrl}`;

            const _thumbnails = (inModal?: boolean) => {
                const Tag = inModal ? 'button' : 'div';

                return [
                    // the list of images...
                    (this._mediaElementsVisible.map((el, index) =>
                        <Tag
                            class={{
                                'ti-media-gallery-thumbnails-item': true,
                                'ti-media-gallery-thumbnails-item-is-active': this.selectedIndex === index,
                                'ti-media-gallery-thumbnails-item-video': el.dataset.mediaType == 'video',
                                'is-hidden': !inModal
                                    && !this.showAllThumbnails
                                    && (this._mediaElementsVisible.length > this.columns && index > this.columns - 2)
                            }}
                            onClick={(event) => this._thumbnailImageClick(event, index)}
                        >
                            {el.dataset.mediaThumbnailUrl
                                ?
                                /* Unique key needed for rerender when video API returns empty */
                                <ti-image key={`thumbnail-${_baseJsxKey(el)}-${inModal}`} src={el.dataset.mediaThumbnailUrl} ratio='rectangle' alt={el.dataset.mediaDesc}
                                ></ti-image>
                                :
                                <div class="ti-media-gallery-thumbnails-item-video-is-loading"><ti-progress></ti-progress></div>
                            }
                        </Tag>
                    )),
                    // ...and the 'more' button if needed
                    (this._mediaElementsVisible.length > this.columns && !this.showAllThumbnails && !inModal &&
                        <div class='ti-media-gallery-thumbnails-item' onClick={() => this.showAllThumbnails = true}>
                            <div>+{this._mediaElementsVisible.length - (this.columns - 1)}</div>
                        </div>
                    )
                ];
            };

            const _bullets = () => {
                return (
                    <div class='ti-media-gallery-bullet-list'>
                        {this._mediaElementsVisible.map((el, index) =>
                            <div
                                class={{
                                    'ti-media-gallery-bullet': el.dataset.mediaType != 'video',
                                    'ti-media-gallery-bullet-video': el.dataset.mediaType == 'video',
                                    'ti-media-gallery-bullet-is-active': this.selectedIndex === index
                                }}
                                onClick={() => this._bulletClick(index)}
                            >
                            </div>
                        )}
                    </div>
                );
            };

            const activeItemEl = this._mediaElementsVisible[this.selectedIndex];
            const activeItemImageSrc = activeItemEl.dataset.mediaImageUrl;
            const activeItemIsVideo = activeItemEl.dataset.mediaType === 'video';

            const imageAnnouncer = `Image ${this.selectedIndex  + 1} of ${this._mediaElements.length}`;

            return (
                <Host>
                    <div
                        role='figure'
                        aria-label='test'
                        class={{
                            'ti-media-gallery-primary-image': true,
                            'ti-media-gallery-video-still-image': activeItemIsVideo
                        }}
                    >

                        {/* Display area */}
                        <div class='ti-media-gallery-scroll-container'
                            role='button'
                            aria-roledescription='carousel'
                            aria-label='Navigate images with the arrow keys, or click to zoom the image'
                            // aria-description={`${imageAnnouncer}`}
                            onClick={() => this._openGallerydialog()}
                            onKeyDown={(ev) => this._handleKeyDown(ev)}
                            ref={(el) => this._carouselScrollElement = el as HTMLElement}>

                            {this._mediaElementsVisible.map((el) =>
                                <div
                                    class={{
                                        'ti-media-gallery-scroll-item': true,
                                        'ti-media-gallery-video-still-image': el?.dataset.mediaType == 'video'
                                    }}
                                    aria-hidden='true'
                                >
                                    <ti-image
                                        key={`image-${_baseJsxKey(el)}`}
                                        src={el.dataset.mediaImageUrl}
                                        ratio='rectangle'
                                        alt={el.dataset.mediaDesc}
                                    ></ti-image>
                                </div>
                            )}
                        </div>

                        <div class='ti-media-gallery-scroll-announcer' aria-live='polite'>{imageAnnouncer}</div>

                        {/* Left button */}
                        <button
                            aria-label="Previous image"
                            class={{
                                'ti-media-gallery-button-disabled': this.selectedIndex === 0,
                                'ti-media-gallery-button-left': true
                            }}
                            onClick={() => this._chevronClick('left')}
                            disabled={this.selectedIndex === 0}
                        >
                            <ti-svg-icon size='l'>
                                chevron-left
                            </ti-svg-icon>
                        </button>

                        {/* Right button */}
                        <button
                            aria-label="Next image"
                            class={{
                                'ti-media-gallery-button-disabled': this.selectedIndex === (this._mediaElementsVisible.length - 1),
                                'ti-media-gallery-button-right': true
                            }}
                            onClick={() => this._chevronClick('right')}
                            disabled={this.selectedIndex === (this._mediaElementsVisible.length - 1)}
                        >
                            <ti-svg-icon size='l'>
                                chevron-right
                            </ti-svg-icon>
                        </button>
                    </div>

                    {/* Thumbnails / Bullets */}
                    <div class='ti-media-gallery-thumbnails'>
                        {this.viewportSize === TiResponsiveViewport.phone
                            ?
                            _bullets()
                            :
                            _thumbnails(false)
                        }
                    </div>

                    {/* Caption Text */}
                    {this.captionText &&
                        <div class='ti-media-gallery-caption'>
                            {this.captionText}
                        </div>
                    }

                    {/* Dialog */}
                    <ti-dialog modal mobile-edgetoedge fullscreen ref={(el) => this._modalElement = el as HTMLTiDialogElement}>

                        {/* Dialog title */}
                        <div slot="title" class="ti-media-gallery-dialog-header">
                            {/* Dialog title */}
                            {activeItemIsVideo
                                ? <h2><slot name="dialog-title-video" /></h2>
                                : <h2 innerHTML={activeItemEl.dataset.mediaDesc}></h2>
                            }
                            {/* File-download button for images */}
                            {!activeItemIsVideo && this.showDownloadLink &&
                                <ti-file-download
                                    fileSrc={activeItemImageSrc}
                                    fileName={activeItemEl.dataset.mediaName}
                                    onClick={() => this._trackFileDownloadMetrics()}
                                >
                                    <a href={activeItemImageSrc} target="_blank">
                                        <ti-svg-icon icon-set="actions" size="s" appearance="secondary">download</ti-svg-icon>
                                        <slot name="download-label" />
                                    </a>
                                </ti-file-download>
                            }
                        </div>

                        {/* Dialog content */}
                        <div class="ti-media-gallery-dialog-content">
                            <div class="ti-media-gallery-dialog-multimedia">
                                {/* Media Element in Dialog */}
                                {activeItemIsVideo
                                    ?
                                    <div class="ti-media-gallery-dialog-multimedia-container">
                                        <div class="ti-media-gallery-dialog-multimedia-video-player">
                                            {/* Video in Dialog */}
                                            <slot name="media" />
                                        </div>
                                        <div class="ti-media-gallery-dialog-multimedia-text-content">
                                            {/* Video Title */}
                                            <h4 class="ti-media-gallery-dialog-multimedia-video-title" innerHTML={activeItemEl.dataset.mediaDesc}></h4>
                                            {/* Video Link */}
                                            {activeItemEl.dataset.videoTranscriptUrl &&
                                                <div class="ti-media-gallery-dialog-multimedia-video-link">
                                                    <a href={activeItemEl.dataset.videoTranscriptUrl} onClick={() => this._trackViewTranscriptMetrics()}>
                                                        <slot name="video-transcript-link-text" />
                                                    </a>
                                                </div>
                                            }
                                            {/* Video Description */}
                                            <p class="ti-media-gallery-dialog-multimedia-video-description" innerHTML={activeItemEl.dataset.videoDesc}></p>
                                        </div>
                                    </div>
                                    :
                                    <img src={activeItemImageSrc} alt={activeItemEl.dataset.mediaDesc} />
                                }

                            </div>

                            {/* Dialog scrollable thumbnails */}
                            <div class="ti-media-gallery-dialog-carousel" ref={(el) => this._modalCarouselElement = el as HTMLElement}>
                                <div class="ti-media-gallery-dialog-carousel-button" onClick={() => this._carouselScroll('left')}><ti-svg-icon size="l">chevron-left</ti-svg-icon></div>
                                <div class="ti-media-gallery-dialog-carousel-thumbnails" ref={(el) => this._modalScrollElement = el as HTMLElement}>{_thumbnails(true)}</div>
                                <div class="ti-media-gallery-dialog-carousel-button" onClick={() => this._carouselScroll('right')}><ti-svg-icon size="l">chevron-right</ti-svg-icon></div>
                            </div>

                            {/* Dialog caption text */}
                            {this.captionText &&
                                <div class='ti-media-gallery-dialog-caption'>
                                    {this.captionText}
                                </div>
                            }
                        </div>
                    </ti-dialog>
                </Host>
            );
        }
    }
}
